import React, { useState, useEffect } from 'react';
import rbacApi from '../../api/rbacApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Snackbar,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Alert,
  Autocomplete,
  FormHelperText,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Search,
  FilterList,
  Group,
  Download,
  Person,
  AdminPanelSettings,
  Close,
  Check,
  Warning,
  Folder,
  Lock
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { dsVar, ink } from '../../theme/tokens';

// No mock data - using real API data only

interface UserGroupsManagementProps {
  canCreateGroups?: boolean;
  canManageGroups?: boolean;
  canDeleteGroups?: boolean;
}

const UserGroupsManagement: React.FC<UserGroupsManagementProps> = ({ 
  canCreateGroups = true, 
  canManageGroups = true, 
  canDeleteGroups = true 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    selectedAdmins: [] as string[],
    selectedMembers: [] as string[]
  });
  const [manageGroup, setManageGroup] = useState<any>(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [canEditProject, setCanEditProject] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [userGroupPermissions, setUserGroupPermissions] = useState<Map<string, boolean>>(new Map());
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [updatingProjects, setUpdatingProjects] = useState<Set<string>>(new Set());
  const [updatingUserPermissions, setUpdatingUserPermissions] = useState<Set<string>>(new Set());
  const [userOperationType, setUserOperationType] = useState<Map<string, string>>(new Map());

  // Permission check functions
  const isOrgAdmin = () => {
    const result = userRole === 'organisation_admin';
    console.log('isOrgAdmin check:', { userRole, result });
    return result;
  };
  const isGroupAdmin = () => {
    const result = userRole === 'user_group_admin';
    console.log('isGroupAdmin check:', { userRole, result });
    return result;
  };
  const canCreateGroup = () => {
    const result = isOrgAdmin() || isGroupAdmin();
    console.log('canCreateGroup check:', { isOrgAdmin: isOrgAdmin(), isGroupAdmin: isGroupAdmin(), result });
    return result;
  };
  const canManageGroup = (group: any) => {
    if (!group || !group.id) {
      console.log('canManageGroup: group is null or missing id', group);
      return false;
    }
    if (isOrgAdmin()) return true;
    
    // Check if user is admin of this specific group using proper API data
    const canManage = userGroupPermissions.get(group.id) || false;
    
    console.log('canManageGroup check:', { 
      groupId: group.id, 
      groupName: group.name, 
      userEmail,
      canManage,
      groupAdmins: group.admins,
      userGroupPermissions: Object.fromEntries(userGroupPermissions) 
    });
    
    return canManage;
  };
  const canViewGroup = () => true; // All users can view groups

  // Load current user info and permissions
  const loadUserInfo = async () => {
    try {
      const email = localStorage.getItem('user_email') || '';
      setUserEmail(email);
      
      // Fetch current user data from API
      const response = await rbacApi.getCurrentUser(email || null as any);
      console.log('User API Response:', response); // Debug log
      
      if (response && response.user && response.user.role) {
        console.log('Setting role from response.user.role:', response.user.role);
        setUserRole(response.user.role);
        console.log('Final user role set to:', response.user.role);
      } else if (response && response.role) {
        console.log('Setting role from response.role:', response.role);
        setUserRole(response.role);
        console.log('Final user role set to:', response.role);
      } else if (response && response.userRole) {
        console.log('Setting role from response.userRole:', response.userRole);
        setUserRole(response.userRole);
        console.log('Final user role set to:', response.userRole);
      } else {
        // Fallback to localStorage if API fails
        const role = localStorage.getItem('user_role') || '';
        console.log('Setting role from localStorage:', role);
        setUserRole(role);
        console.log('Final user role set to:', role);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      // Fallback to localStorage if API fails
      const email = localStorage.getItem('user_email') || '';
      const role = localStorage.getItem('user_role') || '';
      setUserEmail(email);
      setUserRole(role);
    }
  };

  // Load user's group permissions from the groups list
  const loadUserGroupPermissions = () => {
    if (!userEmail) {
      console.log('loadUserGroupPermissions: No userEmail provided');
      return;
    }
    
    console.log('Loading user group permissions for:', userEmail);
    console.log('Available groups:', userGroups);
    
    const permissionsMap = new Map();
    userGroups.forEach((group: any) => {
      if (group && group.id && group.admins) {
        const isAdmin = group.admins.includes(userEmail);
        permissionsMap.set(group.id, isAdmin);
        console.log(`Group "${group.name}" (${group.id}): isAdmin = ${isAdmin}, admins = ${group.admins}`);
      }
    });
    
    setUserGroupPermissions(permissionsMap);
    console.log('User group permissions loaded:', Object.fromEntries(permissionsMap));
  };

  const loadProjects = async () => {
    try {
      // First try to get all projects with a high limit
      const response = await rbacApi.getProjects({ 
        page: 1, 
        limit: 1000  // Set high limit to get all projects
      });
      
      if (response && response.projects) {
        setProjects(response.projects);
        console.log(`Loaded ${response.projects.length} projects for group management`);
        
        // If we got exactly 1000 projects, there might be more
        if (response.projects.length === 1000) {
          console.warn('Got exactly 1000 projects. There might be more projects available.');
        }
      } else if (Array.isArray(response)) {
        setProjects(response);
        console.log(`Loaded ${response.length} projects for group management`);
      } else {
        console.warn('No projects found in response:', response);
        setProjects([]);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setProjects([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, group: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroup(group);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroup(null);
  };

  const handleCreateGroup = () => {
    setCreateDialogOpen(true);
  };

  const handleManageGroup = async () => {
    setManageGroup(selectedGroup);
    setManageDialogOpen(true);
    setAdminSearchTerm('');
    setMemberSearchTerm('');
    setError('');
    
    // Use projects data that's already loaded in the group object
    let currentProjects = selectedGroup?.projects || [];
    
    // If no projects found in group data, try to fetch from API
    if (currentProjects.length === 0 && selectedGroup?.id) {
      try {
        const response = await rbacApi.getGroupProjects(selectedGroup.id);
        if (response && response.projects) {
          currentProjects = response.projects.map((project: any) => project._id || project.id);
        }
      } catch (err) {
        console.error('Error fetching group projects from API:', err);
      }
    }
    
    setSelectedProjects(currentProjects);
    
    handleMenuClose();
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewGroup({
      name: '',
      description: '',
      selectedAdmins: [],
      selectedMembers: []
    });
    setError('');
  };

  const handleCloseManageDialog = () => {
    setManageDialogOpen(false);
    setManageGroup(null);
    setError('');
    setSelectedProjects([]);
  };

  const handleProjectToggle = async (projectId: string) => {
    if (!manageGroup) return;

    const isCurrentlySelected = selectedProjects.includes(projectId);
    
    // Add to updating set
    setUpdatingProjects(prev => new Set(prev).add(projectId));
    
    try {
      if (isCurrentlySelected) {
        // Remove project from group
        await rbacApi.removeProjectFromGroup(manageGroup.id, projectId);
        setSelectedProjects(prev => prev.filter(id => id !== projectId));
        setSuccess(`Removed project from group successfully!`);
      } else {
        // Add project to group
        await rbacApi.addProjectToGroup(manageGroup.id, { projectId });
        setSelectedProjects(prev => [...prev, projectId]);
        setSuccess(`Added project to group successfully!`);
      }
      
      // Refresh groups list to update the ProjectsManagement component
      await loadUserGroups();
    } catch (error) {
      console.error('Error updating project-group association:', error);
      setError(`Failed to ${isCurrentlySelected ? 'remove' : 'add'} project. Please try again.`);
    } finally {
      // Remove from updating set
      setUpdatingProjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
    }
  };

  const handleSaveGroup = async () => {
    if (!newGroup.name.trim()) {
      setError('Group name is required');
      return;
    }
    if (newGroup.selectedAdmins.length === 0) {
      setError('At least one group admin is required');
      return;
    }

    try {
      setError('');
      setCreatingGroup(true);
      const groupData = {
        name: newGroup.name.trim(),
        description: newGroup.description?.trim() || '',
        admins: newGroup.selectedAdmins,
        members: newGroup.selectedMembers,
      };

      await rbacApi.createGroup(groupData);
      setSuccess('User group created successfully');
      handleCloseCreateDialog();
      loadUserGroups(); // Refresh the list
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group. Please try again.');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleSaveManageGroup = async () => {
    if (manageGroup && manageGroup.admins.length === 0) {
      setError('At least one group admin is required');
      return;
    }

    try {
      setError('');
      // Update group with admins and members only (projects are handled separately)
      await rbacApi.updateGroup(manageGroup.id, {
        admins: manageGroup.admins,
        members: manageGroup.members,
      });
      
      handleCloseManageDialog();
      loadUserGroups(); // Refresh the list
      setSuccess('Group updated successfully!');
    } catch (err) {
      console.error('Error updating group:', err);
      setError('Failed to update group. Please try again.');
    }
  };

  const handleDemoteAdmin = async (adminName: string) => {
    if (manageGroup) {
      try {
        // Add user to updating permissions set
        setUpdatingUserPermissions(prev => new Set(prev).add(adminName));
        setUserOperationType(prev => new Map(prev).set(adminName, 'demoting'));
        
        // Call API to demote user from group admins
        await rbacApi.removeGroupAdmin(manageGroup.id, adminName);
        
        // Get all projects associated with this group
        const groupProjects = manageGroup.projects || [];
        
        // Ensure user still has view access to all group projects (as member)
        if (groupProjects.length > 0) {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
          
          // Update permissions for each project individually
          for (const projectId of groupProjects) {
            try {
              await rbacApi.batchUpdateProjectPermissions({
                projectId: projectId,
                organisationId,
                updatedBy: currentUserEmail,
                assignments: [{
                  userId: adminName,
                  permissions: {
                    canView: true, // Keep view access as member
                    canEdit: false, // Remove edit access
                    canManageUsers: false // Remove admin access
                  }
                }]
              });
              console.log(`✅ Updated permissions for ${adminName} for project ${projectId} (demoted to member)`);
            } catch (permError) {
              console.error(`❌ Error updating permissions for ${adminName} for project ${projectId}:`, permError);
              // Continue with other projects even if one fails
            }
          }
        }
        
        const updatedGroup = {
          ...manageGroup,
          admins: manageGroup.admins.filter((admin: string) => admin !== adminName),
          members: manageGroup.members.includes(adminName) ? manageGroup.members : [...manageGroup.members, adminName]
        };
        
        setManageGroup(updatedGroup);
        setSuccess(`Demoted ${adminName} to member and updated project permissions`);
      } catch (error) {
        console.error('Error demoting admin:', error);
        setError(`Failed to demote ${adminName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Remove user from updating permissions set
        setUpdatingUserPermissions(prev => {
          const newSet = new Set(prev);
          newSet.delete(adminName);
          return newSet;
        });
        setUserOperationType(prev => {
          const newMap = new Map(prev);
          newMap.delete(adminName);
          return newMap;
        });
      }
    }
  };

  const handleRemoveAdmin = async (adminName: string) => {
    if (manageGroup) {
      try {
        // Add user to updating permissions set
        setUpdatingUserPermissions(prev => new Set(prev).add(adminName));
        setUserOperationType(prev => new Map(prev).set(adminName, 'removing'));
        
        // Call API to remove user from group admins
        await rbacApi.removeGroupAdmin(manageGroup.id, adminName);
        
        // Get all projects associated with this group
        const groupProjects = manageGroup.projects || [];
        
        // Remove user's project permissions for all group projects
        if (groupProjects.length > 0) {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
          
          // Remove permissions for each project using batch-update-project-permissions
          for (const projectId of groupProjects) {
            try {
              await rbacApi.batchUpdateProjectPermissions({
                projectId: projectId,
                organisationId,
                updatedBy: currentUserEmail,
                assignments: [{
                  userId: adminName,
                  permissions: {
                    canView: false, // Remove view access
                    canEdit: false, // Remove edit access
                    canManageUsers: false // Remove admin access
                  }
                }]
              });
              console.log(`✅ Removed project permissions for ${adminName} from project ${projectId}`);
            } catch (permError) {
              console.error(`❌ Error removing project permissions for ${adminName} from project ${projectId}:`, permError);
              // Continue with other projects even if one fails
            }
          }
        }
        
        const updatedGroup = {
          ...manageGroup,
          admins: manageGroup.admins.filter((admin: string) => admin !== adminName)
        };
        setManageGroup(updatedGroup);
        setSuccess(`Removed ${adminName} from admin role and cleaned up project permissions`);
      } catch (error) {
        console.error('Error removing admin:', error);
        setError(`Failed to remove ${adminName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Remove user from updating permissions set
        setUpdatingUserPermissions(prev => {
          const newSet = new Set(prev);
          newSet.delete(adminName);
          return newSet;
        });
        setUserOperationType(prev => {
          const newMap = new Map(prev);
          newMap.delete(adminName);
          return newMap;
        });
      }
    }
  };

  const handleAddAdmin = async (userEmail: string) => {
    if (manageGroup) {
      try {
        // Add user to updating permissions set
        setUpdatingUserPermissions(prev => new Set(prev).add(userEmail));
        setUserOperationType(prev => new Map(prev).set(userEmail, 'promoting'));
        
        // Call API to add user to group admins
        await rbacApi.addGroupAdmin(manageGroup.id, { userId: userEmail });
        
        // Get all projects associated with this group
        const groupProjects = manageGroup.projects || [];
        
        // Grant view access to all group projects for the new admin
        if (groupProjects.length > 0) {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
          
          // Update permissions for each project individually
          for (const projectId of groupProjects) {
            try {
              await rbacApi.batchUpdateProjectPermissions({
                projectId: projectId,
                organisationId,
                updatedBy: currentUserEmail,
                assignments: [{
                  userId: userEmail,
                  permissions: {
                    canView: true, // Grant view access to all group projects
                    canEdit: false, // Default to no edit access (can be granted separately)
                    canManageUsers: false // Default to no admin access (can be granted separately)
                  }
                }]
              });
              console.log(`✅ Granted view access to ${userEmail} for project ${projectId}`);
            } catch (permError) {
              console.error(`❌ Error granting view access to ${userEmail} for project ${projectId}:`, permError);
              // Continue with other projects even if one fails
            }
          }
          
          console.log(`✅ Granted view access to ${userEmail} for ${groupProjects.length} projects in group ${manageGroup.name}`);
        }
        
        const updatedGroup = {
          ...manageGroup,
          admins: [...manageGroup.admins, userEmail],
          members: manageGroup.members.includes(userEmail) ? manageGroup.members : [...manageGroup.members, userEmail]
        };
        setManageGroup(updatedGroup);
        setSuccess(`Promoted ${userEmail} to admin and granted view access to ${groupProjects.length} projects`);
      } catch (error) {
        console.error('Error promoting to admin:', error);
        setError(`Failed to promote ${userEmail} to admin: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Remove user from updating permissions set
        setUpdatingUserPermissions(prev => {
          const newSet = new Set(prev);
          newSet.delete(userEmail);
          return newSet;
        });
        setUserOperationType(prev => {
          const newMap = new Map(prev);
          newMap.delete(userEmail);
          return newMap;
        });
      }
    }
  };

  const handleAddMember = async (userEmail: string) => {
    if (manageGroup) {
      try {
        // Add user to updating permissions set
        setUpdatingUserPermissions(prev => new Set(prev).add(userEmail));
        setUserOperationType(prev => new Map(prev).set(userEmail, 'adding'));
        
        // Call API to add user to group
        await rbacApi.addGroupMember(manageGroup.id, { userId: userEmail });
        
        // Get all projects associated with this group
        const groupProjects = manageGroup.projects || [];
        
        // Grant view access to all group projects for the new member
        if (groupProjects.length > 0) {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
          
          // Update permissions for each project individually
          for (const projectId of groupProjects) {
            try {
              await rbacApi.batchUpdateProjectPermissions({
                projectId: projectId,
                organisationId,
                updatedBy: currentUserEmail,
                assignments: [{
                  userId: userEmail,
                  permissions: {
                    canView: true, // Grant view access to all group projects
                    canEdit: false, // Default to no edit access
                    canManageUsers: false // Default to no admin access
                  }
                }]
              });
              console.log(`✅ Granted view access to ${userEmail} for project ${projectId}`);
            } catch (permError) {
              console.error(`❌ Error granting view access to ${userEmail} for project ${projectId}:`, permError);
              // Continue with other projects even if one fails
            }
          }
          
          console.log(`✅ Granted view access to ${userEmail} for ${groupProjects.length} projects in group ${manageGroup.name}`);
        }
        
        const updatedGroup = {
          ...manageGroup,
          members: manageGroup.members.includes(userEmail) ? manageGroup.members : [...manageGroup.members, userEmail]
        };
        setManageGroup(updatedGroup);
        setSuccess(`Added ${userEmail} to group and granted view access to ${groupProjects.length} projects`);
      } catch (error) {
        console.error('Error adding member:', error);
        setError(`Failed to add ${userEmail} to group: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Remove user from updating permissions set
        setUpdatingUserPermissions(prev => {
          const newSet = new Set(prev);
          newSet.delete(userEmail);
          return newSet;
        });
        setUserOperationType(prev => {
          const newMap = new Map(prev);
          newMap.delete(userEmail);
          return newMap;
        });
      }
    }
  };

  const handleRemoveMember = async (memberName: string) => {
    if (manageGroup) {
      try {
        // Add user to updating permissions set
        setUpdatingUserPermissions(prev => new Set(prev).add(memberName));
        setUserOperationType(prev => new Map(prev).set(memberName, 'removing'));
        
        // Call API to remove user from group
        await rbacApi.removeGroupMember(manageGroup.id, memberName);
        
        // Get all projects associated with this group
        const groupProjects = manageGroup.projects || [];
        
        // Remove user's project permissions for all group projects
        if (groupProjects.length > 0) {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
          
          // Remove permissions for each project using batch-update-project-permissions
          for (const projectId of groupProjects) {
            try {
              await rbacApi.batchUpdateProjectPermissions({
                projectId: projectId,
                organisationId,
                updatedBy: currentUserEmail,
                assignments: [{
                  userId: memberName,
                  permissions: {
                    canView: false, // Remove view access
                    canEdit: false, // Remove edit access
                    canManageUsers: false // Remove admin access
                  }
                }]
              });
              console.log(`✅ Removed project permissions for ${memberName} from project ${projectId}`);
            } catch (permError) {
              console.error(`❌ Error removing project permissions for ${memberName} from project ${projectId}:`, permError);
              // Continue with other projects even if one fails
            }
          }
        }
        
        const updatedGroup = {
          ...manageGroup,
          members: manageGroup.members.filter((member: string) => member !== memberName),
          admins: manageGroup.admins.filter((admin: string) => admin !== memberName) // Also remove from admins if they were admin
        };
        setManageGroup(updatedGroup);
        setSuccess(`Removed ${memberName} from group and cleaned up project permissions`);
      } catch (error) {
        console.error('Error removing member:', error);
        setError(`Failed to remove ${memberName} from group: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        // Remove user from updating permissions set
        setUpdatingUserPermissions(prev => {
          const newSet = new Set(prev);
          newSet.delete(memberName);
          return newSet;
        });
        setUserOperationType(prev => {
          const newMap = new Map(prev);
          newMap.delete(memberName);
          return newMap;
        });
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  // Load user groups from backend
  const loadUserGroups = async () => {
    try {
      setLoading(true);
      const response = await rbacApi.getGroups({ search: searchTerm });
      
      
      if (response && Array.isArray(response)) {
        // Real data from backend (already mapped by rbacApi)
        setUserGroups(response);
      } else if (response && response.userGroups) {
        // Raw backend response - map it
        const mappedGroups = response.userGroups.map((group: any) => {
          // Dynamic field mapping - handle different possible field names
          const groupId = group._id || group.id || group.groupId || group.group_id;
          const groupName = group.name || group.groupName || group.title || group.group_name;
          const groupDescription = group.description || group.desc || group.groupDescription || group.group_description;
          const groupAdmins = group.admins || group.adminUsers || group.admin_users || group.administrators || [];
          const groupMembers = group.members || group.memberUsers || group.member_users || group.users || [];
          const groupStatus = group.active !== undefined ? (group.active ? 'active' : 'inactive') : 
                             (group.status || group.groupStatus || 'active');
          const groupCreatedBy = group.createdBy || group.created_by || group.owner || group.createdByUser;
          const groupCreatedAt = group.createdAt || group.created_at || group.createdDate;
          const groupUpdatedAt = group.updatedAt || group.updated_at || group.modifiedAt;
          // Handle projects array - extract IDs from project objects
          let groupProjects = group.projects || group.groupProjects || group.associatedProjects || [];
          if (Array.isArray(groupProjects) && groupProjects.length > 0) {
            // If projects are objects with _id, extract the IDs
            if (typeof groupProjects[0] === 'object' && groupProjects[0]._id) {
              groupProjects = groupProjects.map((project: any) => project._id || project.id);
            }
          }
          
          return {
            id: groupId,
            name: groupName || 'Unknown Group',
            description: groupDescription || 'No description available',
            admins: groupAdmins,
            members: groupMembers,
            projects: groupProjects,
            status: groupStatus,
            organisationId: localStorage.getItem('org_id') || 'fixmyclaim',
            createdBy: groupCreatedBy || 'Unknown',
            createdAt: groupCreatedAt,
            updatedAt: groupUpdatedAt,
            memberCount: groupMembers.length,
            adminCount: groupAdmins.length
          };
        });
        setUserGroups(mappedGroups);
      } else {
        // No user groups found
        setUserGroups([]);
      }
    } catch (err) {
      console.error('Error loading user groups:', err);
      setUserGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Load all users for selection
  const loadAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await rbacApi.getUsers();
      
      if (response && Array.isArray(response)) {
        // Real data from backend (already mapped)
        setAllUsers(response);
      } else if (response && response.users) {
        // Raw backend response - map it
        const mappedUsers = response.users.map((user: any) => {
          // Dynamic field mapping - handle different possible field names
          const userId = user._id || user.id || user.userId || user.user_id;
          const userName = user.userName || user.name || user.fullName || user.displayName || user.username;
          const userEmail = user.emailId || user.email || user.emailAddress || user.email_address;
          const userRole = user.role || user.userRole || user.user_role || user.permission;
          const userStatus = user.status || user.userStatus || user.user_status || user.active;
          
          // Dynamic role mapping
          let mappedRole = 'user';
          if (userRole) {
            const role = userRole.toString().toUpperCase();
            if (['ADMIN', 'ORGANISATION_ADMIN', 'ORG_ADMIN'].includes(role)) {
              mappedRole = 'organisation_admin';
            } else if (['USER_GROUP_ADMIN', 'GROUP_ADMIN', 'TEAM_ADMIN'].includes(role)) {
              mappedRole = 'user_group_admin';
            }
          }
          
          return {
            id: userId,
            name: userName || 'Unknown User',
            email: userEmail || 'no-email@example.com',
            role: mappedRole,
            status: userStatus === 'ACTIVE' ? 'active' : 'inactive'
          };
        });
        setAllUsers(mappedUsers);
      } else {
        setAllUsers([]);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setAllUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Debug effect to log role changes
  useEffect(() => {
    console.log('User role changed to:', userRole);
    console.log('Permission checks:', {
      isOrgAdmin: isOrgAdmin(),
      isGroupAdmin: isGroupAdmin(),
      canCreateGroup: canCreateGroup()
    });
  }, [userRole]);

  // Load user group permissions when userGroups or userEmail changes
  useEffect(() => {
    if (userEmail && userGroups.length > 0) {
      loadUserGroupPermissions();
    }
  }, [userEmail, userGroups]);

  // Load data on component mount
  useEffect(() => {
    loadUserInfo();
    loadUserGroups();
    loadAllUsers();
    loadProjects();
  }, [searchTerm]);


  const filteredGroups = userGroups
    .filter(group => group && group.id) // Remove null/undefined groups
    .filter(group =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const filteredAdmins = allUsers.filter(user =>
    user.name.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(adminSearchTerm.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredMembers = allUsers.filter(user =>
    user.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(memberSearchTerm.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h6" sx={{ color: dsVar('fg'), fontWeight: 700 }}>
          User Groups
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search groups..."
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: dsVar('surface'),
                color: dsVar('fg'),
                borderRadius: '8px',
                '& fieldset': { borderColor: dsVar('line') },
                '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
              },
              '& .MuiInputBase-input::placeholder': { color: dsVar('fg-2'), opacity: 1 }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: dsVar('fg-2') }} />
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {canCreateGroup() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateGroup}
              sx={{
                bgcolor: dsVar('accent'),
                color: dsVar('on-accent'),
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: dsVar('accent'),
                  boxShadow: '0 0 12px rgba(255, 222, 89, 0.3)'
                }
              }}
            >
              Create Group
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={12}>
          <CircularProgress sx={{ color: dsVar('accent-fg') }} />
        </Box>
      ) : filteredGroups.length === 0 ? (
        <Card sx={{ bgcolor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '16px', py: 8, textAlign: 'center' }}>
          <Group sx={{ fontSize: 64, color: dsVar('fg-faint'), mb: 2 }} />
          <Typography sx={{ color: dsVar('fg-2') }}>No user groups found.</Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredGroups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card sx={{ 
                height: '100%',
                bgcolor: dsVar('surface'), 
                border: `1px solid ${dsVar('line')}`, 
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: dsVar('accent-fg'),
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ bgcolor: 'rgba(255, 222, 89, 0.1)', color: dsVar('accent-fg'), border: '1px solid rgba(255, 222, 89, 0.2)' }}>
                        <Group />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ color: dsVar('fg'), fontWeight: 700, fontSize: '1rem' }}>
                          {group.name}
                        </Typography>
                        <Chip 
                          label={group.status?.toUpperCase() || 'ACTIVE'} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.65rem', 
                            fontWeight: 700,
                            bgcolor: group.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            color: group.status === 'active' ? ink('#10b981') : ink('#f59e0b'),
                            border: `1px solid ${group.status === 'active' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`
                          }} 
                        />
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, group)} sx={{ color: dsVar('fg-2') }}>
                      <MoreVert />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="body2" sx={{ color: dsVar('fg-2'), mb: 3, lineBreak: 'anywhere', minHeight: '3em' }}>
                    {group.description}
                  </Typography>

                  <Divider sx={{ borderColor: dsVar('line'), mb: 2 }} />
                  
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <Box>
                      <Typography sx={{ color: dsVar('fg-muted'), fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Members</Typography>
                      <Typography sx={{ color: dsVar('fg'), fontWeight: 700 }}>{group.memberCount}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: dsVar('fg-muted'), fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Admins</Typography>
                      <Typography sx={{ color: dsVar('fg'), fontWeight: 700 }}>{group.adminCount}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Group Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: dsVar('surface'),
            border: `1px solid ${dsVar('line')}`,
            color: dsVar('fg'),
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              py: 1,
              '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.05)', color: dsVar('accent-fg') }
            }
          }
        }}
      >
        <MenuItem onClick={handleManageGroup}>
          <ListItemIcon sx={{ color: 'inherit' }}><AdminPanelSettings fontSize="small" /></ListItemIcon>
          <ListItemText primary="Manage Group" />
        </MenuItem>
        {isOrgAdmin() && (
          <MenuItem sx={{ color: ink('#ef4444'), '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05) !important' } }}>
            <ListItemIcon sx={{ color: 'inherit' }}><Delete fontSize="small" /></ListItemIcon>
            <ListItemText primary="Delete Group" />
          </MenuItem>
        )}
      </Menu>

      {/* Create Group Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: dsVar('surface'), backgroundImage: 'none', border: `1px solid ${dsVar('line')}`, borderRadius: '16px' }
        }}
      >
        <DialogTitle sx={{ color: dsVar('fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>
          Create New Group
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', color: ink('#ef4444'), border: '1px solid rgba(239, 68, 68, 0.2)' }}>{error}</Alert>}
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { color: dsVar('fg-2') },
                  '& .MuiOutlinedInput-root': {
                    color: dsVar('fg'),
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { color: dsVar('fg-2') },
                  '& .MuiOutlinedInput-root': {
                    color: dsVar('fg'),
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${dsVar('line')}` }}>
          <Button onClick={handleCloseCreateDialog} sx={{ color: dsVar('fg-2'), textTransform: 'none' }}>Cancel</Button>
          <Button 
            onClick={handleSaveGroup} 
            variant="contained" 
            disabled={creatingGroup || !newGroup.name.trim()}
            sx={{ 
              bgcolor: dsVar('accent'), 
              color: dsVar('on-accent'), 
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { bgcolor: dsVar('accent') },
              '&.Mui-disabled': { bgcolor: dsVar('line'), color: dsVar('fg-2') }
            }}
          >
            {creatingGroup ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Group Dialog */}
      <Dialog 
        open={manageDialogOpen} 
        onClose={handleCloseManageDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: dsVar('surface'), backgroundImage: 'none', border: `1px solid ${dsVar('line')}`, borderRadius: '16px' }
        }}
      >
        <DialogTitle sx={{ color: dsVar('fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Manage Group: {manageGroup?.name}
          <IconButton onClick={handleCloseManageDialog} sx={{ color: dsVar('fg-2') }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={4} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle1" sx={{ color: dsVar('accent-fg'), fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" /> Members & Admins
              </Typography>
              <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '12px', overflow: 'hidden', bgcolor: dsVar('page'), maxHeight: 400, overflowY: 'auto' }}>
                <List sx={{ p: 0 }}>
                  {manageGroup?.admins.map((email: string) => (
                    <ListItem 
                      key={email}
                      secondaryAction={
                        <Box display="flex" gap={1}>
                          <Chip label="ADMIN" size="small" sx={{ bgcolor: 'rgba(255, 222, 89, 0.1)', color: dsVar('accent-fg'), fontWeight: 700, fontSize: '0.65rem' }} />
                          <IconButton size="small" onClick={() => handleDemoteAdmin(email)} disabled={updatingUserPermissions.has(email)} sx={{ color: dsVar('fg-2') }}>
                            {updatingUserPermissions.has(email) ? <CircularProgress size={16} sx={{ color: dsVar('accent-fg') }} /> : <Lock />}
                          </IconButton>
                        </Box>
                      }
                      sx={{ borderBottom: `1px solid ${dsVar('line')}`, bgcolor: 'rgba(255, 222, 89, 0.02)' }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: dsVar('line'), color: dsVar('accent-fg') }}>{email.charAt(0).toUpperCase()}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={<Typography sx={{ color: dsVar('fg'), fontWeight: 600 }}>{email}</Typography>} />
                    </ListItem>
                  ))}
                  {manageGroup?.members.filter((m: string) => !manageGroup.admins.includes(m)).map((email: string) => (
                    <ListItem 
                      key={email}
                      secondaryAction={
                        <Box display="flex" gap={1}>
                          <IconButton size="small" onClick={() => handleAddAdmin(email)} disabled={updatingUserPermissions.has(email)} sx={{ color: dsVar('accent-fg') }}>
                            {updatingUserPermissions.has(email) ? <CircularProgress size={16} sx={{ color: dsVar('accent-fg') }} /> : <AdminPanelSettings />}
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemoveMember(email)} disabled={updatingUserPermissions.has(email)} sx={{ color: ink('#ef4444') }}>
                            {updatingUserPermissions.has(email) ? <CircularProgress size={16} sx={{ color: ink('#ef4444') }} /> : <Delete />}
                          </IconButton>
                        </Box>
                      }
                      sx={{ borderBottom: `1px solid ${dsVar('line')}` }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: dsVar('line'), color: dsVar('fg-2') }}>{email.charAt(0).toUpperCase()}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={<Typography sx={{ color: dsVar('fg') }}>{email}</Typography>} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" sx={{ color: dsVar('accent-fg'), fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Folder fontSize="small" /> Group Projects
              </Typography>
              <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '12px', bgcolor: dsVar('page'), maxHeight: 400, overflowY: 'auto' }}>
                <List sx={{ p: 0 }}>
                  {projects.map((project) => (
                    <ListItem 
                      key={project.id}
                      secondaryAction={
                        updatingProjects.has(project.id) ? (
                          <CircularProgress size={20} sx={{ color: dsVar('accent-fg') }} />
                        ) : (
                          <Checkbox 
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleProjectToggle(project.id)}
                            sx={{ color: dsVar('fg-faint'), '&.Mui-checked': { color: dsVar('accent-fg') } }}
                          />
                        )
                      }
                      sx={{ borderBottom: `1px solid ${dsVar('line')}` }}
                    >
                      <ListItemText primary={<Typography sx={{ color: dsVar('fg'), fontSize: '0.875rem' }}>{project.name}</Typography>} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${dsVar('line')}` }}>
          <Button onClick={handleCloseManageDialog} sx={{ color: dsVar('fg-2'), textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
        sx={{ '& .MuiSnackbarContent-root': { bgcolor: '#10b981', color: '#fff', fontWeight: 600 } }}
      />
    </Box>
  );
};

export default UserGroupsManagement; 